import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Sparkles, DollarSign, FileCheck, Home, Layers, Droplet, Zap, Hammer, Square, Fence, PaintBucket, Box } from "lucide-react";
import type { TemplateLayout } from "@/types/proposals";
import { nanoid } from "nanoid";

interface QuickStartTemplatesProps {
  onSelectTemplate: (layout: TemplateLayout, name: string) => void;
}

export function QuickStartTemplates({ onSelectTemplate }: QuickStartTemplatesProps) {
  const templates = [
    {
      id: "roofr-modern",
      name: "Modern Minimal",
      description: "Clean lines • Bold typography • Professional",
      icon: Home,
      category: "Roofing" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: {
              fontFamily: "montserrat" as const,
              backgroundColor: "rgba(15, 23, 42, 0.85)",
            },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "PROFESSIONAL ROOFING SOLUTIONS",
              serviceType: "Roof Replacement",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#667eea",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "roofr-classic",
      name: "Classic Elegance",
      description: "Warm tones • Traditional • Trustworthy",
      icon: Home,
      category: "Roofing" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: {
              fontFamily: "playfair" as const,
              backgroundColor: "rgba(30, 20, 10, 0.75)",
            },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Excellence in Roofing Since 1995",
              serviceType: "Roof Replacement",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#f5576c",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "roofr-bold",
      name: "Bold Contemporary",
      description: "Striking • Dynamic • Premium",
      icon: Zap,
      category: "Roofing" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: {
              fontFamily: "raleway" as const,
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "ARCHITECTURAL ROOFING MASTERY",
              serviceType: "Roof Replacement",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#00f2fe",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // SIDING TEMPLATES
    {
      id: "siding-modern",
      name: "Modern Minimal",
      description: "Contemporary • Sleek • Professional",
      icon: Layers,
      category: "Siding" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(15, 23, 42, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "PREMIUM SIDING SOLUTIONS",
              serviceType: "Siding Replacement",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#38f9d7",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "siding-classic",
      name: "Classic Elegance",
      description: "Timeless • Refined • Quality",
      icon: Layers,
      category: "Siding" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(120, 65, 20, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Quality Siding, Timeless Beauty",
              serviceType: "Siding Replacement",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#fee140",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "siding-bold",
      name: "Bold Contemporary",
      description: "Architectural • Striking • Modern",
      icon: Layers,
      category: "Siding" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(0, 75, 85, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "EXTERIOR TRANSFORMATION EXPERTS",
              serviceType: "Siding Replacement",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#330867",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // GUTTER TEMPLATES
    {
      id: "gutters-modern",
      name: "Modern Minimal",
      description: "Seamless • Clean • Professional",
      icon: Droplet,
      category: "Gutters" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(15, 23, 42, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "SEAMLESS GUTTER SYSTEMS",
              serviceType: "Gutter Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#fed6e3",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "gutters-classic",
      name: "Classic Elegance",
      description: "Traditional • Reliable • Trusted",
      icon: Droplet,
      category: "Gutters" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(140, 70, 20, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Protecting Homes for Generations",
              serviceType: "Gutter Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#fecfef",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "gutters-bold",
      name: "Bold Contemporary",
      description: "Precision • Heavy-Duty • Guaranteed",
      icon: Droplet,
      category: "Gutters" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(0, 20, 80, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "PRECISION GUTTER INSTALLATION",
              serviceType: "Gutter Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#fcb69f",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // DECKING TEMPLATES
    {
      id: "decking-modern",
      name: "Modern Minimal",
      description: "Contemporary • Outdoor Living • Professional",
      icon: Hammer,
      category: "Decking" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(55, 30, 10, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "OUTDOOR LIVING EXPERTS",
              serviceType: "Deck Construction",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#d97706",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "decking-classic",
      name: "Classic Elegance",
      description: "Traditional • Craftsmanship • Quality",
      icon: Hammer,
      category: "Decking" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(92, 50, 18, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Masterful Deck Building Since 1990",
              serviceType: "Deck Construction",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#f59e0b",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "decking-bold",
      name: "Bold Contemporary",
      description: "Modern Design • Premium Materials • Architectural",
      icon: Hammer,
      category: "Decking" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(20, 15, 10, 0.8)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "ARCHITECTURAL DECK DESIGN",
              serviceType: "Deck Construction",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#fb923c",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // WINDOWS TEMPLATES
    {
      id: "windows-modern",
      name: "Modern Minimal",
      description: "Energy-Efficient • Contemporary • Professional",
      icon: Square,
      category: "Windows" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(3, 105, 161, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "ENERGY-EFFICIENT SOLUTIONS",
              serviceType: "Window Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#0284c7",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "windows-classic",
      name: "Classic Elegance",
      description: "Timeless • Quality • Trusted",
      icon: Square,
      category: "Windows" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(30, 64, 175, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Quality Windows, Lasting Beauty",
              serviceType: "Window Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#3b82f6",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "windows-bold",
      name: "Bold Contemporary",
      description: "Modern • Sleek • Performance",
      icon: Square,
      category: "Windows" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(7, 89, 133, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "PRECISION WINDOW SYSTEMS",
              serviceType: "Window Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#0ea5e9",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // FENCING TEMPLATES
    {
      id: "fencing-modern",
      name: "Modern Minimal",
      description: "Contemporary • Secure • Professional",
      icon: Fence,
      category: "Fencing" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(20, 83, 45, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "PROPERTY BOUNDARY EXPERTS",
              serviceType: "Fence Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#16a34a",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "fencing-classic",
      name: "Classic Elegance",
      description: "Traditional • Durable • Quality",
      icon: Fence,
      category: "Fencing" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(34, 70, 30, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Quality Fencing, Lasting Security",
              serviceType: "Fence Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#22c55e",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "fencing-bold",
      name: "Bold Contemporary",
      description: "Modern • Striking • Secure",
      icon: Fence,
      category: "Fencing" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(21, 128, 61, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "ARCHITECTURAL FENCE SYSTEMS",
              serviceType: "Fence Installation",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#4ade80",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // PAINTING TEMPLATES
    {
      id: "painting-modern",
      name: "Modern Minimal",
      description: "Contemporary • Clean • Professional",
      icon: PaintBucket,
      category: "Painting" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(67, 20, 120, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "COLOR YOUR WORLD",
              serviceType: "Painting Services",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#7c3aed",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "painting-classic",
      name: "Classic Elegance",
      description: "Traditional • Quality • Trusted",
      icon: PaintBucket,
      category: "Painting" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(88, 28, 135, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Quality Painting Since 1985",
              serviceType: "Painting Services",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#a855f7",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "painting-bold",
      name: "Bold Contemporary",
      description: "Vibrant • Modern • Transformative",
      icon: PaintBucket,
      category: "Painting" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(107, 33, 168, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "TRANSFORMATION THROUGH COLOR",
              serviceType: "Painting Services",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#c084fc",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    // CONCRETE TEMPLATES
    {
      id: "concrete-modern",
      name: "Modern Minimal",
      description: "Contemporary • Durable • Professional",
      icon: Box,
      category: "Concrete" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "montserrat" as const, backgroundColor: "rgba(51, 65, 85, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "SOLID FOUNDATIONS",
              serviceType: "Concrete Work",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#64748b",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "concrete-classic",
      name: "Classic Elegance",
      description: "Traditional • Reliable • Trusted",
      icon: Box,
      category: "Concrete" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "playfair" as const, backgroundColor: "rgba(71, 85, 105, 0.75)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "Building Strong Foundations Since 1980",
              serviceType: "Concrete Work",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#94a3b8",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "concrete-bold",
      name: "Bold Contemporary",
      description: "Industrial • Modern • Precision",
      icon: Box,
      category: "Concrete" as const,
      layout: {
        pages: [{
          id: nanoid(),
          title: "Cover",
          order: 1,
          blocks: [{
            id: nanoid(),
            type: "cover" as const,
            order: 1,
            position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
            style: { fontFamily: "raleway" as const, backgroundColor: "rgba(30, 41, 59, 0.85)" },
            content: {
              companyName: "{{org_name}}",
              companyTagline: "PRECISION CONCRETE SOLUTIONS",
              serviceType: "Concrete Work",
              customerName: "{{customer_name}}",
              customerAddress: "{{property_address}}",
              date: "{{current_date}}",
              brandColor: "#cbd5e1",
            },
          }],
        }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "blank",
      name: "Blank Canvas",
      description: "Start from scratch",
      icon: FileText,
      layout: {
        pages: [{ id: nanoid(), title: "Page 1", order: 1, blocks: [] }],
        variables: [],
        styles: {},
      },
    },
    {
      id: "simple",
      name: "Simple Proposal",
      description: "Cover + Text + Line Items + Signature",
      icon: Sparkles,
      layout: {
        pages: [
          {
            id: nanoid(),
            title: "Page 1",
            order: 1,
            blocks: [
              {
                id: nanoid(),
                type: "cover" as const,
                order: 1,
                position: { x: 0, y: 0, width: 816, height: 400, zIndex: 0 },
                content: {
                  companyName: "{{org_name}}",
                  companyTagline: "Professional Proposal",
                  serviceType: "Service Quote",
                  customerName: "{{customer_name}}",
                  customerAddress: "{{property_address}}",
                  date: "{{current_date}}",
                  brandColor: "#2BC5B4",
                },
              },
              {
                id: nanoid(),
                type: "text" as const,
                order: 2,
                position: { x: 48, y: 450, width: 720, height: "auto" as const, zIndex: 1 },
                content: { text: "Thank you for considering our services..." },
              },
              {
                id: nanoid(),
                type: "line_items_table" as const,
                order: 3,
                position: { x: 48, y: 600, width: 720, height: "auto" as const, zIndex: 1 },
                content: { items: [], showUnitPrice: true, showQuantity: true, showTotal: true },
              },
              {
                id: nanoid(),
                type: "signature" as const,
                order: 4,
                position: { x: 48, y: 800, width: 400, height: 150, zIndex: 1 },
                content: { label: "Customer Signature" },
              },
            ],
          },
        ],
        variables: [],
        styles: {},
      },
    },
    {
      id: "premium",
      name: "Premium Proposal",
      description: "Cover + Gallery + Packages + Terms",
      icon: DollarSign,
      layout: {
        pages: [
          {
            id: nanoid(),
            title: "Cover",
            order: 1,
            blocks: [
              {
                id: nanoid(),
                type: "cover" as const,
                order: 1,
                position: { x: 0, y: 0, width: 816, height: 1056, zIndex: 0 },
                content: {
                  companyName: "{{org_name}}",
                  companyTagline: "Premium Roofing Solutions",
                  serviceType: "Premium Service",
                  customerName: "{{customer_name}}",
                  customerAddress: "{{property_address}}",
                  date: "{{current_date}}",
                  brandColor: "#764ba2",
                },
              },
            ],
          },
          {
            id: nanoid(),
            title: "Packages",
            order: 2,
            blocks: [
              {
                id: nanoid(),
                type: "good_better_best" as const,
                order: 1,
                position: { x: 48, y: 100, width: 720, height: "auto" as const, zIndex: 1 },
                content: { options: [] },
              },
            ],
          },
        ],
        variables: [],
        styles: {},
      },
    },
    {
      id: "quote",
      name: "One-Page Quote",
      description: "Compact layout for quick quotes",
      icon: FileCheck,
      layout: {
        pages: [
          {
            id: nanoid(),
            title: "Quote",
            order: 1,
            blocks: [
              {
                id: nanoid(),
                type: "text" as const,
                order: 1,
                position: { x: 48, y: 48, width: 720, height: "auto" as const, zIndex: 1 },
                style: { fontSize: 24, fontWeight: "bold" as const },
                content: { text: "{{org_name}} - Quick Quote" },
              },
              {
                id: nanoid(),
                type: "line_items_table" as const,
                order: 2,
                position: { x: 48, y: 150, width: 720, height: "auto" as const, zIndex: 1 },
                content: { items: [], showUnitPrice: true, showQuantity: true, showTotal: true },
              },
              {
                id: nanoid(),
                type: "totals_summary" as const,
                order: 3,
                position: { x: 500, y: 600, width: 268, height: "auto" as const, zIndex: 1 },
                content: { subtotal: 0, tax: 0, discount: 0, total: 0 },
              },
            ],
          },
        ],
        variables: [],
        styles: {},
      },
    },
  ];

  // Group templates by category
  const roofingTemplates = templates.filter(t => 'category' in t && t.category === "Roofing");
  const sidingTemplates = templates.filter(t => 'category' in t && t.category === "Siding");
  const gutterTemplates = templates.filter(t => 'category' in t && t.category === "Gutters");
  const deckingTemplates = templates.filter(t => 'category' in t && t.category === "Decking");
  const windowsTemplates = templates.filter(t => 'category' in t && t.category === "Windows");
  const fencingTemplates = templates.filter(t => 'category' in t && t.category === "Fencing");
  const paintingTemplates = templates.filter(t => 'category' in t && t.category === "Painting");
  const concreteTemplates = templates.filter(t => 'category' in t && t.category === "Concrete");
  const otherTemplates = templates.filter(t => !('category' in t));

  const renderServiceSection = (serviceTemplates: typeof templates, title: string, description: string) => (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-1 bg-gradient-to-b from-primary to-primary/50 rounded-full" />
        <h3 className="font-bold text-sm tracking-wide uppercase text-foreground">{title}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3 pl-3">{description}</p>
      <div className="space-y-2">
        {serviceTemplates.map((template) => {
          const Icon = template.icon;
          return (
            <Card key={template.id} className="hover:border-primary/50 hover:shadow-lg transition-all cursor-pointer group overflow-hidden">
              <CardHeader className="p-3 pb-2">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm font-semibold">{template.name}</CardTitle>
                    <CardDescription className="text-xs mt-0.5">{template.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <Button variant="default" size="sm" className="w-full group-hover:bg-primary/90" onClick={() => onSelectTemplate(template.layout, template.name)}>
                  Use Template
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 p-4">
      {renderServiceSection(roofingTemplates, "Roofing", "Premium templates for roofing proposals")}
      {renderServiceSection(sidingTemplates, "Siding", "Professional siding proposal templates")}
      {renderServiceSection(gutterTemplates, "Gutters", "Complete gutter system templates")}
      {renderServiceSection(deckingTemplates, "Decking", "Outdoor living & deck construction templates")}
      {renderServiceSection(windowsTemplates, "Windows", "Energy-efficient window installation templates")}
      {renderServiceSection(fencingTemplates, "Fencing", "Property boundary & security templates")}
      {renderServiceSection(paintingTemplates, "Painting", "Interior & exterior painting templates")}
      {renderServiceSection(concreteTemplates, "Concrete", "Foundation & flatwork templates")}

      {/* Other Templates Section */}
      <div>
        <h3 className="font-semibold text-sm mb-3 text-muted-foreground">Other Templates</h3>
        <div className="space-y-2">
          {otherTemplates.map((template) => {
            const Icon = template.icon;
            return (
              <Card key={template.id} className="hover:border-primary/50 transition-colors cursor-pointer">
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm">{template.name}</CardTitle>
                      <CardDescription className="text-xs">{template.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => onSelectTemplate(template.layout, template.name)}
                  >
                    Use Template
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
